import { Combobox, Select, TextInput } from "@msuite/ui/form";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { StatusIndicator } from "@msuite/ui/status-indicator";
import type { ContactMemberSchema } from "@msuite/utils/schemas";
import React from "react";
import { useFormContext } from "react-hook-form";
import { useContacts } from "@/hooks/use-contacts";
import { useTableTypes } from "@/hooks/use-table-types";

/** Props Interface */
interface ContactMemberFormProps {
	isContactPickerDisabled?: boolean;
}

export function ContactMemberForm({ isContactPickerDisabled = false }: ContactMemberFormProps) {
	/** Context */
	const methods = useFormContext<ContactMemberSchema>();

	/** Hooks */
	const { t } = useTranslation();
	const { data: memberTypes } = useTableTypes("contactMemberTypes", ["active"]);
	const { data: authorizationTypes } = useTableTypes("contactMemberAuthorizationTypes", ["active"]);
	const { contacts } = useContacts();

	/** Render */
	return (
		<React.Fragment>
			<Combobox
				control={methods.control}
				name="contactId"
				options={contacts}
				displayValue={(item) => item.name}
				label={t("contacts.contact")}
				keyExtractor={(item) => item.id}
				placeholder={t("placeholders.company_name")}
				isRequired={false}
				data-cy="contact-member-contact-picker"
				isDisabled={isContactPickerDisabled}
				isNullable={!isContactPickerDisabled}
			/>
			<Select
				control={methods.control}
				name="contactMemberTypeId"
				autoFocus
				label={t("general.type")}
				isRequired
				isNullable={false}
				itemNotFoundLabel={t("general.select")}
				data-cy="contact-member-type-select"
				options={
					memberTypes?.tableTypes.map((type) => ({
						label: type.name,
						value: type.id,
						item: type,
					})) || []
				}
				renderOption={(item) => (
					<div className="h-stack">
						<StatusIndicator color={item.color} />
						<p>{item.name}</p>
					</div>
				)}
			/>
			<Select
				label={t("profile.title")}
				control={methods.control}
				name="title"
				isRequired={false}
				isNullable={false}
				itemNotFoundLabel={t("general.not_found")}
				data-cy="contact-member-title-select"
				options={[
					{ label: t("profile.mr"), value: "mr", item: { value: "mr" } },
					{ label: t("profile.mrs"), value: "mrs", item: { value: "mrs" } },
				]}
			/>
			<div className="gap-6 grid grid-cols-2">
				<TextInput
					label={t("profile.first_name")}
					control={methods.control}
					placeholder={t("placeholders.first_name")}
					name="firstName"
					isRequired
					data-cy="contact-member-first-name-input"
				/>
				<TextInput
					label={t("profile.last_name")}
					control={methods.control}
					placeholder={t("placeholders.last_name")}
					name="lastName"
					isRequired
					data-cy="contact-member-last-name-input"
				/>
			</div>
			<TextInput
				label={t("personnel.fields.email")}
				control={methods.control}
				placeholder={t("placeholders.email")}
				name="email"
				isRequired={false}
				leftIcon={icon("AtSign")}
				data-cy="contact-member-email-input"
			/>
			<TextInput
				label={t("personnel.fields.phone")}
				control={methods.control}
				placeholder={t("placeholders.phone")}
				name="phone"
				isRequired={false}
				leftIcon={icon("Phone")}
				data-cy="contact-member-phone-input"
			/>
			<Select
				name="authorizations"
				control={methods.control}
				label={t("contacts.member_authorizations")}
				isRequired={false}
				multiple
				multipleSelectionRender={(p) => (
					<div className="v-stack items-start">
						<p className="whitespace-pre text-left">{p.map((p) => p.item?.name).join("\n")}</p>
					</div>
				)}
				options={
					authorizationTypes?.tableTypes.map((type) => ({
						label: type.name,
						value: type.id,
						item: type,
					})) || []
				}
				itemNotFoundLabel={t("general.select")}
				data-cy="contact-member-authorizations-select"
			/>
		</React.Fragment>
	);
}
